import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";
import AuthShell, { AuthField } from "../components/auth/AuthShell";
import { GameButton, GameIcon } from "../components/game";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  usePageTitle(t("auth.forgot_password_title", "Resetare parolă"));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { identifier: email.trim() });
      setSubmitted(true);
    } catch (e: any) {
      // The backend always answers 200 for a found-or-not account, to avoid
      // email enumeration — so an error here is never "no such user". It is
      // either the rate limit or a real connectivity problem, and those need
      // different things from the person: wait, or try again.
      if (e?.response?.status === 429) {
        setErr(
          t(
            "auth.forgot_password_throttled",
            "Prea multe cereri de resetare într-un timp scurt. Așteaptă o oră și încearcă din nou — sau cere clubului să îți reseteze parola."
          )
        );
      } else {
        setErr(t("auth.forgot_password_error", "Nu s-a putut trimite cererea. Încearcă din nou."));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      icon={<GameIcon name="bulb" className="w-8 h-8" />}
      title={t("auth.forgot_password_title", "Resetare parolă")}
      subtitle={
        submitted
          ? undefined
          : t(
              "auth.forgot_password_subtitle",
              "Introdu emailul sau username-ul contului. Îți trimitem un link pentru a alege o parolă nouă. Pentru un sportiv fără email, linkul ajunge pe adresa părintelui."
            )
      }
    >
      {submitted ? (
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-100 grid place-items-center">
            <GameIcon name="check" className="w-8 h-8 text-emerald-600" />
          </div>
          <p className="text-slate-600 text-sm leading-relaxed">
            {t(
              "auth.forgot_password_sent",
              "Dacă există un cont asociat, vei primi în câteva minute un link de resetare pe email (pentru un sportiv fără email, pe adresa părintelui). Verifică și folderul Spam."
            )}
          </p>
          {/* The way out when no email can reach them at all — a child with no
              mailbox whose parent has none either. Without this line they keep
              checking an inbox that will never get anything. */}
          <p className="mt-3 text-xs leading-relaxed text-slate-500">
            {t(
              "auth.forgot_password_no_mailbox",
              "Nu primești nimic nici după 10 minute? Dacă nu ai o adresă de email proprie, cere administratorului clubului să îți reseteze parola din contul tău."
            )}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {err && (
            <div className="p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">{err}</div>
          )}
          <AuthField
            label={t("auth.email_or_username", "Email sau username")}
            type="text"
            name="forgot-password-identifier"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            placeholder={t("auth.email_or_username_ph", "nume@email.com sau username")}
          />
          <GameButton type="submit" disabled={loading || !email.trim()}>
            {loading ? t("common.loading", "Se trimite...") : t("auth.forgot_password_submit", "Trimite link de resetare")}
          </GameButton>
        </form>
      )}

      <div className="mt-6 text-sm text-center">
        <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors">
          {t("auth.back_to_login", "Înapoi la login")}
        </Link>
      </div>
    </AuthShell>
  );
}
